import React, { useState, useEffect, useCallback, useMemo } from "react";
import { IncomeForm, IncomeList } from "../components/income";
import { Income, Expense, Group, ExpenseSection } from "../lib/types";
import { incomeUtils, expenseUtils } from "../lib";
import { addBusinessDays } from "../lib/bankingDateUtils";
import { toLocalDatetimeInputValue } from "../lib/dateUtils";
import { incomeAPI, expenseAPI } from "../services/api";
import incomeDB from "../services/database/incomeDB";
import { demoService } from "../services/demo/demoService";
import { groupAPI, shareAPI, integrationsAPI } from "../services/api";
import FloatingActionMenu from "../components/common/FloatingActionMenu";
import { ShareRecordModal } from "../components/groups";
import LoadingTimerDebug from "../components/common/LoadingTimerDebug";
import { EmptyState, SearchFilterBar } from "../components/common";
import PageShareModal from "../components/common/PageShareModal";
import { useShareAction } from "../contexts/ShareActionContext";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Drawer,
  IconButton,
  useTheme,
  Typography,
  Fab,
  Tooltip,
  Snackbar,
  TextField,
  Skeleton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";

interface IncomePageProps {
  setPageTitle?: (title: string) => void;
}

type PaymentProcessorOption = { label: string; inputValue?: string };

const basePaymentProcessorLabels = [
  "Stripe",
  "PayPal",
  "Bank Transfer",
  "Check",
  "Cash",
  "Zelle",
  "Venmo",
  "Zoho",
  "Other",
];

const IncomePage: React.FC<IncomePageProps> = ({ setPageTitle }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const [searchParams, setSearchParams] = useSearchParams();
  const { setShareHandler } = useShareAction();
  const paymentProcessorFilter = createFilterOptions<PaymentProcessorOption>();
  const [income, setIncome] = useState<Income[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);

  const [isEditing, setIsEditing] = useState(false);
  const [currentIncome, setCurrentIncome] = useState<Income | undefined>(
    undefined
  );

  // State for received modal
  const [isReceivedModalOpen, setIsReceivedModalOpen] = useState(false);
  const [incomeToMarkReceived, setIncomeToMarkReceived] = useState<
    Income | undefined
  >(undefined);
  const [receivedDate, setReceivedDate] = useState("");
  const [depositDate, setDepositDate] = useState("");
  const [paymentProcessor, setPaymentProcessor] = useState("");
  const [paymentProcessorInput, setPaymentProcessorInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [appliedSearchQuery, setAppliedSearchQuery] = useState("");
  const [paidFilter, setPaidFilter] = useState<"all" | "paid" | "unpaid">(
    "all"
  );
  const [recurringFilter, setRecurringFilter] = useState<
    "all" | "recurring" | "one-time"
  >("all");
  const [sharedOnly, setSharedOnly] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareIncome, setShareIncome] = useState<Income | null>(null);
  const [groups, setGroups] = useState<Group[]>([]);
  const [expandedSection, setExpandedSection] = useState<ExpenseSection | null>(
    null
  );
  const [pageShareOpen, setPageShareOpen] = useState(false);
  const paymentProcessorOptions = useMemo((): {
    label: string;
    inputValue?: string;
  }[] => {
    const labels = new Set(
      basePaymentProcessorLabels.map((label) => label.trim()).filter(Boolean)
    );
    income.forEach((item) => {
      if (item.paymentProcessor) {
        labels.add(item.paymentProcessor.trim());
      }
    });
    return Array.from(labels)
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b))
      .map((label) => ({ label }));
  }, [income]);

  const updateSectionParam = useCallback(
    (section: ExpenseSection | null) => {
      const next = new URLSearchParams(searchParams);
      if (section) {
        next.set("section", section);
      } else {
        next.delete("section");
      }
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  useEffect(() => {
    const sectionParam = searchParams.get("section");
    if (sectionParam && sectionParam !== expandedSection) {
      setExpandedSection(sectionParam as ExpenseSection);
    }
    if (!sectionParam && expandedSection) {
      setExpandedSection(null);
    }
    const shareParam = searchParams.get("share");
    if (shareParam === "1" && !pageShareOpen) {
      setPageShareOpen(true);
    }
  }, [searchParams, expandedSection, pageShareOpen]);
  const [allExpanded, setAllExpanded] = useState(false);
  const [isSyncingTpc, setIsSyncingTpc] = useState(false);
  const [incomeToPostpone, setIncomeToPostpone] = useState<Income | null>(null);
  const [postponeUntil, setPostponeUntil] = useState<string>("");
  const [postponeError, setPostponeError] = useState<string | null>(null);
  const [incomeToReschedule, setIncomeToReschedule] = useState<Income | null>(
    null
  );
  const [rescheduleTo, setRescheduleTo] = useState<string>("");
  const [rescheduleError, setRescheduleError] = useState<string | null>(null);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  // Undo functionality
  type UndoAction =
    | { type: "add"; income: Income }
    | { type: "update"; previousIncome: Income; newIncome: Income };
  const [undoStack, setUndoStack] = useState<UndoAction[]>([]);
  const [undoSnackbar, setUndoSnackbar] = useState<{
    open: boolean;
    message: string;
    // Present when the action can be reversed, e.g. a delete that is held in
    // the recovery window on both sides.
    onUndo?: () => void;
  }>({ open: false, message: "" });

  const normalizeDateTimeInput = useCallback((value: string): string => {
    const trimmed = value.trim();
    if (!trimmed) return "";
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      return `${trimmed}T00:01`;
    }
    return trimmed;
  }, []);

  const onOpen = () => {
    setIsEditing(false);
    setCurrentIncome(undefined);
    setIsOpen(true);
  };
  const onClose = () => setIsOpen(false);

  // Set page title
  useEffect(() => {
    if (setPageTitle) {
      setPageTitle("Income");
    }
  }, [setPageTitle]);

  useEffect(() => {
    setShareHandler(() => {
      const next = new URLSearchParams(searchParams);
      next.set("share", "1");
      setSearchParams(next, { replace: true });
      setPageShareOpen(true);
    });
    return () => setShareHandler(null);
  }, [searchParams, setSearchParams, setShareHandler]);

  // Undo handler
  const handleUndo = useCallback(async () => {
    if (undoStack.length === 0) return;

    const lastAction = undoStack[undoStack.length - 1];
    try {
      if (lastAction.type === "add") {
        // Undo add: delete the income
        await incomeAPI.delete(lastAction.income.id);
        setIncome((prev) => prev.filter((i) => i.id !== lastAction.income.id));
        setUndoSnackbar({
          open: true,
          message: `Undone: "${lastAction.income.client}" removed`,
        });
      } else if (lastAction.type === "update") {
        // Undo update: restore previous version
        const restored = await incomeAPI.update(lastAction.previousIncome);
        if (typeof restored.expectedByDate === "string") {
          restored.expectedByDate = new Date(restored.expectedByDate);
        }
        setIncome((prev) =>
          prev.map((i) => (i.id === restored.id ? restored : i))
        );
        setUndoSnackbar({
          open: true,
          message: `Undone: "${lastAction.previousIncome.client}" restored`,
        });
      }
      // Remove the action from the stack
      setUndoStack((prev) => prev.slice(0, -1));
    } catch (error) {
      console.error("Failed to undo:", error);
      setUndoSnackbar({ open: true, message: "Failed to undo action" });
    }
  }, [undoStack]);

  // Keyboard listener for Ctrl+Z / Cmd+Z
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "z" && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleUndo]);

  // Records mirrored from TPC Account Center only changed when someone opened
  // Integrations and pressed Sync, so a payment logged upstream never showed
  // here. Pull on mount, and expose the same pull as a button.
  const shapeIncomeRows = useCallback(
    (rows: Income[]): Income[] =>
      rows.map((row) => {
        const shaped = { ...row };
        if (typeof row.expectedByDate === "string") {
          shaped.expectedByDate = new Date(row.expectedByDate);
        }
        if (typeof row.receivedDate === "string") {
          shaped.receivedDate = new Date(row.receivedDate);
        }
        if (typeof row.depositDate === "string") {
          shaped.depositDate = new Date(row.depositDate);
        }
        if (typeof row.invoiceTotal === "string") {
          shaped.invoiceTotal = parseFloat(row.invoiceTotal) || 0;
        }
        if (typeof row.paidToDate === "string") {
          shaped.paidToDate = parseFloat(row.paidToDate) || 0;
        }
        if (!shaped.type) shaped.type = "Actual";
        return shaped;
      }),
    []
  );

  const refreshFromTpc = useCallback(
    async (announce: boolean) => {
      setIsSyncingTpc(true);
      try {
        const result = await integrationsAPI.syncTpc();
        console.log("[IncomePage] TPC sync result:", result);
        const rows = await incomeAPI.getAll();
        setIncome(shapeIncomeRows(rows));
        if (announce) {
          setUndoSnackbar({
            open: true,
            message: `Synced from Account Center — ${result.total} record${
              result.total === 1 ? "" : "s"
            } checked.`,
          });
        }
      } catch (error) {
        // Not being connected to TPC is the normal case for most users, so the
        // automatic pull stays silent; an explicit press still reports.
        console.debug("[IncomePage] TPC sync skipped:", error);
        if (announce) {
          setUndoSnackbar({
            open: true,
            message:
              error instanceof Error
                ? error.message
                : "Could not sync from Account Center.",
          });
        }
      } finally {
        setIsSyncingTpc(false);
      }
    },
    [shapeIncomeRows]
  );

  useEffect(() => {
    if (demoService.isDemo()) return;
    refreshFromTpc(false);
  }, [refreshFromTpc]);

  // Load income and expenses from database on initial render
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        const [incomeResult, expensesResult] = await Promise.allSettled([
          incomeAPI.getAll(),
          expenseAPI.getAll(),
        ]);

        const allIncome =
          incomeResult.status === "fulfilled" ? incomeResult.value : [];
        const allExpenses =
          expensesResult.status === "fulfilled" ? expensesResult.value : [];

        if (incomeResult.status === "rejected") {
          console.error(
            "[IncomePage] Failed to load income:",
            incomeResult.reason
          );
        }
        if (expensesResult.status === "rejected") {
          console.error(
            "[IncomePage] Failed to load expenses for net calculation:",
            expensesResult.reason
          );
        }

        // Ensure all expectedByDate are Date objects and amounts are numbers
        const formattedIncome = allIncome.map((income) => {
          const formatted = { ...income };

          // Convert expectedByDate string to Date object
          if (typeof income.expectedByDate === "string") {
            formatted.expectedByDate = new Date(income.expectedByDate);
          }

          // Convert invoiceTotal string to number
          if (typeof income.invoiceTotal === "string") {
            formatted.invoiceTotal = parseFloat(income.invoiceTotal);
          }

          // Convert paidToDate string to number
          if (typeof income.paidToDate === "string") {
            formatted.paidToDate = parseFloat(income.paidToDate);
          }

          // Convert receivedDate string to Date object
          if (typeof income.receivedDate === "string") {
            formatted.receivedDate = new Date(income.receivedDate);
          }

          // Convert depositDate string to Date object
          if (typeof income.depositDate === "string") {
            formatted.depositDate = new Date(income.depositDate);
          }

          if (!formatted.type) {
            formatted.type = "Actual";
          }

          return formatted;
        });

        // Format expenses
        const formattedExpenses = allExpenses.map((expense) => {
          const formatted = { ...expense };
          if (typeof expense.dueDate === "string") {
            formatted.dueDate = new Date(expense.dueDate);
          }
          if (typeof expense.amount === "string") {
            formatted.amount = parseFloat(expense.amount);
          }
          return formatted;
        });

        if (incomeResult.status === "fulfilled") {
          console.log("Loaded income:", formattedIncome);
          setIncome(formattedIncome);
        }
        if (expensesResult.status === "fulfilled") {
          console.log(
            "Loaded expenses for net calculation:",
            formattedExpenses
          );
          setExpenses(formattedExpenses);
        }
      } catch (error) {
        console.error("Failed to load data from database:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();

    // Subscribe to demo mode changes
    const unsubscribe = demoService.subscribe(() => {
      loadData(); // Reload data when demo mode changes
    });

    return unsubscribe;
  }, []);

  const handleAddIncome = async (incomeData: Omit<Income, "id">) => {
    try {
      // Add to database
      const newIncome = await incomeAPI.add(incomeData);

      // Ensure the expectedByDate is a Date object and amounts are numbers
      if (typeof newIncome.expectedByDate === "string") {
        newIncome.expectedByDate = new Date(newIncome.expectedByDate);
      }
      if (typeof newIncome.invoiceTotal === "string") {
        newIncome.invoiceTotal = parseFloat(newIncome.invoiceTotal);
      }
      if (typeof newIncome.paidToDate === "string") {
        newIncome.paidToDate = parseFloat(newIncome.paidToDate);
      }
      if (typeof newIncome.receivedDate === "string") {
        newIncome.receivedDate = new Date(newIncome.receivedDate);
      }
      if (typeof newIncome.depositDate === "string") {
        newIncome.depositDate = new Date(newIncome.depositDate);
      }
      if (!newIncome.type) {
        newIncome.type = "Actual";
      }

      console.log("New income added:", newIncome);

      // Update local state
      setIncome([...income, newIncome]);

      // Track for undo
      setUndoStack((prev) => [...prev, { type: "add", income: newIncome }]);
      setUndoSnackbar({
        open: true,
        message: `Added "${newIncome.client}" - Press Ctrl+Z to undo`,
      });
    } catch (error) {
      console.error("Failed to add income:", error);
      alert("Failed to save income. Please try again.");
      throw error;
    }
  };

  const handleUpdateIncome = async (updatedIncome: Income) => {
    try {
      // Store the previous version for undo
      const previousIncome = income.find((i) => i.id === updatedIncome.id);

      console.log("[IncomePage] Updating income payload:", updatedIncome);
      // Update in database
      const resultIncome = await incomeAPI.update(updatedIncome);

      console.log("Updated income:", resultIncome);

      // Ensure the expectedByDate is a Date object and amounts are numbers
      if (typeof resultIncome.expectedByDate === "string") {
        resultIncome.expectedByDate = new Date(resultIncome.expectedByDate);
      }
      if (typeof resultIncome.invoiceTotal === "string") {
        resultIncome.invoiceTotal = parseFloat(resultIncome.invoiceTotal);
      }
      if (typeof resultIncome.paidToDate === "string") {
        resultIncome.paidToDate = parseFloat(resultIncome.paidToDate);
      }
      if (typeof resultIncome.receivedDate === "string") {
        resultIncome.receivedDate = new Date(resultIncome.receivedDate);
      }
      if (typeof resultIncome.depositDate === "string") {
        resultIncome.depositDate = new Date(resultIncome.depositDate);
      }
      if (!resultIncome.type) {
        resultIncome.type = "Actual";
      }

      // Update local state
      setIncome((prevIncome) =>
        prevIncome.map((income) =>
          income.id === resultIncome.id ? resultIncome : income
        )
      );

      // Track for undo (only if we found the previous version)
      if (previousIncome) {
        setUndoStack((prev) => [
          ...prev,
          { type: "update", previousIncome, newIncome: resultIncome },
        ]);
        setUndoSnackbar({
          open: true,
          message: `Updated "${resultIncome.client}" - Press Ctrl+Z to undo`,
        });
      }

      onClose();
    } catch (error) {
      console.error("Failed to update income:", error);
      alert("Failed to update income. Please try again.");
    }
  };

  const handleEditIncome = (incomeItem: Income) => {
    console.debug("[IncomePage] Edit income", {
      id: incomeItem.id,
      client: incomeItem.client,
    });
    setCurrentIncome(incomeItem);
    setIsEditing(true);
    setIsOpen(true);
  };

  // Income suspended from the Dashboard breakdown is excluded from totals, so
  // it needs somewhere to live where it can be found and brought back.
  // Generated recurring occurrences inherit the series' suspension and have
  // synthetic ids that no row matches, so list the series record once instead.
  const suspendedIncome = useMemo(() => {
    const byId = new Map<string, Income>();
    income
      .filter((inc) => incomeUtils.isIncomeSuspended(inc))
      .forEach((inc) => {
        const baseId = inc.originalId || inc.id;
        const base = income.find((row) => row.id === baseId) || inc;
        byId.set(base.id, base);
      });
    return Array.from(byId.values());
  }, [income]);

  // Postpone: the Income page equivalent of the Dashboard breakdown's pause —
  // hold a record out of the totals until a date, or indefinitely.
  const defaultPostponeDate = (): string => {
    const d = new Date();
    d.setMonth(d.getMonth() + 1);
    return d.toISOString().slice(0, 10);
  };

  const handlePostponeIncome = (target: Income) => {
    const base = income.find(
      (row) => row.id === (target.originalId || target.id)
    );
    const record = base || target;
    if (incomeUtils.isIncomeSuspended(record)) {
      handleUnsuspendIncome(record);
      return;
    }
    setPostponeError(null);
    setPostponeUntil(
      record.suspendedUntil
        ? new Date(record.suspendedUntil).toISOString().slice(0, 10)
        : defaultPostponeDate()
    );
    setIncomeToPostpone(record);
  };

  const applyPostpone = async (mode: "until" | "indefinite") => {
    if (!incomeToPostpone) return;
    let suspendedUntil: string | null = null;
    if (mode === "until") {
      const parsed = new Date(`${postponeUntil}T00:00:00.000Z`);
      if (!postponeUntil || isNaN(parsed.getTime())) {
        setPostponeError("Enter a valid date, or postpone indefinitely.");
        return;
      }
      suspendedUntil = parsed.toISOString();
    }
    try {
      const saved = await incomeDB.updateIncome({
        ...incomeToPostpone,
        isSuspended: true,
        suspendedUntil,
      });
      if (!saved?.isSuspended) {
        throw new Error("The server did not save the postponement.");
      }
      setIncome((prev) =>
        prev.map((inc) =>
          inc.id === incomeToPostpone.id ? { ...inc, ...saved } : inc
        )
      );
      setIncomeToPostpone(null);
    } catch (error) {
      console.error("[IncomePage] Postpone failed:", error);
      setPostponeError(
        error instanceof Error ? error.message : "Failed to postpone income."
      );
    }
  };

  const handleUnsuspendIncome = async (target: Income) => {
    console.log("[IncomePage] Unsuspending income:", target.id);
    try {
      const updated = await incomeDB.updateIncome({
        ...target,
        isSuspended: false,
        suspendedUntil: null,
      });
      if (updated?.isSuspended) {
        throw new Error("The server did not clear the suspension.");
      }
      setIncome((prev) =>
        prev.map((inc) => (inc.id === target.id ? { ...inc, ...updated } : inc))
      );
    } catch (error) {
      console.error("[IncomePage] Unsuspend failed:", error);
      setUndoSnackbar({
        open: true,
        message:
          error instanceof Error
            ? error.message
            : "Failed to unsuspend income.",
      });
    }
  };

  const handleDeleteIncome = async (income: Income) => {
    if (income.isRecurring || income.isRecurringInstance) {
      const deleteSeries = window.confirm(
        `This is a recurring income. Delete the entire series? Click OK to delete all future items in the series. Click Cancel to keep the series. If you only want to remove this one occurrence, use Skip (it removes just this occurrence and keeps future ones).`
      );
      if (!deleteSeries) {
        alert(
          "Skip removes only this occurrence and keeps the rest of the series."
        );
        return;
      }

      const deletePast = window.confirm(
        "Also delete past occurrences? Click OK to delete past items too. Click Cancel to keep past items."
      );

      const baseId = income.originalId || income.id;
      console.log("[IncomePage] Deleting income series:", {
        baseId,
        deletePast,
      });
      try {
        await incomeAPI.deleteSeries(baseId, deletePast);
        setIncome((prevIncome) =>
          prevIncome.filter(
            (i) => i.id !== income.id && i.originalId !== baseId
          )
        );
        return;
      } catch (error) {
        console.error("Failed to delete income series:", error);
        alert("Failed to delete income series. Please try again.");
        return;
      }
    }

    // Records mirrored from Account Center are deleted there as well, so say so
    // before doing it — and say that it can be taken back.
    const isLinked =
      income.sourceProvider === "tpc" &&
      income.sourceDocumentType === "proposal";
    const confirmMessage = isLinked
      ? `Delete this income from ${income.client}?\n\nThis also deletes the linked proposal in TPC Account Center. Both can be restored for 30 days.`
      : `Are you sure you want to delete this income from ${income.client}?`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const result = await incomeAPI.delete(income.id);

      // Update local state - this will also remove all recurring instances when the page refreshes
      setIncome((prevIncome) =>
        prevIncome.filter(
          (i) => i.id !== income.id && i.originalId !== income.id
        )
      );

      const deletedRecordId = result.deletedRecordId;
      setUndoSnackbar({
        open: true,
        message: isLinked
          ? `Deleted here and in Account Center. Restorable for 30 days.`
          : `Deleted ${income.client}.`,
        onUndo: deletedRecordId
          ? async () => {
              try {
                await incomeAPI.restoreDeleted(deletedRecordId);
                const rows = await incomeAPI.getAll();
                setIncome(shapeIncomeRows(rows));
                setUndoSnackbar({ open: true, message: "Restored." });
              } catch (error) {
                console.error("[IncomePage] Restore failed:", error);
                setUndoSnackbar({
                  open: true,
                  message:
                    error instanceof Error
                      ? error.message
                      : "Failed to restore.",
                });
              }
            }
          : undefined,
      });
    } catch (error) {
      console.error("Failed to delete income:", error);
      setUndoSnackbar({
        open: true,
        message:
          error instanceof Error
            ? error.message
            : "Failed to delete income. Please try again.",
      });
    }
  };

  const handleSkipIncome = async (income: Income) => {
    const baseId = income.originalId || income.id;
    const skipDate = new Date(income.expectedByDate);
    const skipDateKey = `${skipDate.getUTCFullYear()}-${String(
      skipDate.getUTCMonth() + 1
    ).padStart(2, "0")}-${String(skipDate.getUTCDate()).padStart(2, "0")}`;
    console.log("[IncomePage] Skipping income occurrence:", {
      baseId,
      skipDate: skipDateKey,
      expectedByDate: income.expectedByDate,
    });
    try {
      await incomeAPI.skipOccurrence(baseId, skipDateKey);
      setIncome((prevIncome) => prevIncome.filter((i) => i.id !== income.id));
    } catch (error) {
      console.error("Failed to skip income occurrence:", error);
      alert("Failed to skip income. Please try again.");
    }
  };

  // "Move to a later date": pushes a single occurrence out without touching the
  // rest of the series. A stored row just gets a new date; a generated
  // occurrence has a synthetic id no row matches, so its original date is
  // skipped on the series and a one-off row is created at the new date.
  const handleRescheduleIncome = (target: Income) => {
    setRescheduleError(null);
    const current = new Date(target.expectedByDate);
    const next = new Date(current);
    next.setMonth(next.getMonth() + 1);
    setRescheduleTo(next.toISOString().slice(0, 10));
    setIncomeToReschedule(target);
  };

  const applyReschedule = async () => {
    if (!incomeToReschedule) return;
    const parsed = new Date(`${rescheduleTo}T00:00:00.000Z`);
    if (!rescheduleTo || isNaN(parsed.getTime())) {
      setRescheduleError("Pick a date to move this to.");
      return;
    }
    const target = incomeToReschedule;
    const isGenerated = Boolean(
      target.isRecurringInstance && target.originalId
    );
    console.log("[IncomePage] Rescheduling income:", {
      id: target.id,
      isGenerated,
      from: target.expectedByDate,
      to: parsed.toISOString(),
    });
    try {
      if (isGenerated) {
        const from = new Date(target.expectedByDate);
        const skipKey = `${from.getUTCFullYear()}-${String(
          from.getUTCMonth() + 1
        ).padStart(2, "0")}-${String(from.getUTCDate()).padStart(2, "0")}`;
        await incomeAPI.skipOccurrence(target.originalId as string, skipKey);
        const { id, isRecurringInstance, originalId, ...rest } = target;
        await incomeDB.addIncome({
          ...rest,
          expectedByDate: parsed,
          isRecurring: false,
          recurrencePattern: undefined,
          notes: `${target.notes || ""}`
            .replace(/\s*\(Recurring instance\)/, "")
            .trim(),
        } as Omit<Income, "id">);
      } else {
        await incomeDB.updateIncome({ ...target, expectedByDate: parsed });
      }
      const rows = await incomeAPI.getAll();
      setIncome(shapeIncomeRows(rows));
      setIncomeToReschedule(null);
    } catch (error) {
      console.error("[IncomePage] Reschedule failed:", error);
      setRescheduleError(
        error instanceof Error ? error.message : "Failed to move this income."
      );
    }
  };

  const handleShareIncome = async (income: Income) => {
    setShareIncome(income);
    setShareOpen(true);

    if (groups.length > 0) {
      return;
    }

    try {
      const availableGroups = await groupAPI.list();
      setGroups(availableGroups);
    } catch (error) {
      console.error("Failed to load groups:", error);
    }
  };

  const handleShareRecord = (
    groupIds: string[],
    permission: "view" | "edit"
  ) => {
    if (!shareIncome) {
      return;
    }

    const incomeIdToShare = shareIncome.id;

    void (async () => {
      try {
        await shareAPI.shareRecord({
          recordType: "income",
          recordId: incomeIdToShare,
          groupIds,
          permission,
        });
        // Update local state to mark income as shared
        setIncome((prev) =>
          prev.map((item) =>
            item.id === incomeIdToShare ? { ...item, isShared: true } : item
          )
        );
        console.log("[IncomePage] Marked income as shared:", incomeIdToShare);
      } catch (error) {
        console.error("Failed to share income:", error);
        alert("Failed to share income. Please try again.");
      }
    })();
  };

  const handleMarkReceived = (income: Income) => {
    // If this is a recurring instance, show a warning
    if (income.isRecurringInstance) {
      alert(
        `This is a recurring instance. To mark recurring income as received, please mark the original item for "${income.client}".`
      );
      return;
    }

    const now = new Date();
    const receivedValue = toLocalDatetimeInputValue(now);
    const depositValue = toLocalDatetimeInputValue(
      addBusinessDays(new Date(now), 5)
    );

    setIncomeToMarkReceived(income);
    setReceivedDate(receivedValue);
    setDepositDate(depositValue);
    const initialProcessor = income.paymentProcessor || "";
    setPaymentProcessor(initialProcessor);
    setPaymentProcessorInput(initialProcessor);
    console.log("[IncomePage] Open mark received modal:", {
      incomeId: income.id,
      client: income.client,
      paymentProcessor: initialProcessor,
      receivedDate: receivedValue,
      depositDate: depositValue,
    });
    setIsReceivedModalOpen(true);
  };

  const handleReceivedSubmit = async () => {
    if (!incomeToMarkReceived || !depositDate) {
      alert("Please fill in all required fields.");
      return;
    }

    try {
      const resolvedProcessor = paymentProcessor.trim() || undefined;
      console.log("[IncomePage] Submitting mark received:", {
        incomeId: incomeToMarkReceived.id,
        receivedDate,
        depositDate,
        paymentProcessor: resolvedProcessor,
      });
      const normalizedReceivedDate = receivedDate
        ? normalizeDateTimeInput(receivedDate)
        : "";
      const normalizedDepositDate = normalizeDateTimeInput(depositDate);
      const updatedIncome = {
        ...incomeToMarkReceived,
        receivedDate: normalizedReceivedDate
          ? new Date(normalizedReceivedDate)
          : undefined,
        depositDate: new Date(normalizedDepositDate),
        paymentProcessor: resolvedProcessor,
        isPaid: true,
      };

      // Update in database
      const resultIncome = await incomeAPI.update(updatedIncome);

      // Ensure dates are properly converted
      if (typeof resultIncome.expectedByDate === "string") {
        resultIncome.expectedByDate = new Date(resultIncome.expectedByDate);
      }
      if (typeof resultIncome.receivedDate === "string") {
        resultIncome.receivedDate = new Date(resultIncome.receivedDate);
      }
      if (typeof resultIncome.depositDate === "string") {
        resultIncome.depositDate = new Date(resultIncome.depositDate);
      }

      // Update local state
      setIncome((prevIncome) =>
        prevIncome.map((income) =>
          income.id === resultIncome.id ? resultIncome : income
        )
      );

      // Close modal
      setIsReceivedModalOpen(false);
      setIncomeToMarkReceived(undefined);
      setReceivedDate("");
      setDepositDate("");
      setPaymentProcessor("");
      setPaymentProcessorInput("");
    } catch (error) {
      console.error("Failed to mark income as received:", error);
      alert("Failed to mark income as received. Please try again.");
    }
  };

  const handleSearchApply = () => {
    setAppliedSearchQuery(searchQuery);
    console.log("[IncomePage] Search applied:", searchQuery);
  };

  const visibleIncome = sharedOnly
    ? income.filter(
        (entry) =>
          (entry as Income & { is_shared?: boolean }).is_shared ||
          entry.isShared
      )
    : income;
  const normalizedSearch = appliedSearchQuery.trim().toLowerCase();
  const filteredIncome = visibleIncome.filter((entry) => {
    const searchValues = [entry.client, entry.invoiceLink].filter(
      (value): value is string => Boolean(value)
    );
    const matchesSearch = !normalizedSearch
      ? true
      : searchValues.some((value) =>
          value.toLowerCase().includes(normalizedSearch)
        );
    const matchesPaid =
      paidFilter === "all"
        ? true
        : paidFilter === "paid"
          ? entry.isPaid
          : !entry.isPaid;
    const matchesRecurring =
      recurringFilter === "all"
        ? true
        : recurringFilter === "recurring"
          ? entry.isRecurring
          : !entry.isRecurring;

    return matchesSearch && matchesPaid && matchesRecurring;
  });
  console.log("[IncomePage] Filtered income:", {
    total: visibleIncome.length,
    filtered: filteredIncome.length,
    search: normalizedSearch,
    paidFilter,
    recurringFilter,
  });
  // Daily-recurring income is an explicit weekday schedule the user wants on
  // the date cards, so treat it as projected income even when its type is
  // Prospective (which is otherwise shown only in the separate Prospective
  // section). Non-daily Prospective income stays in that section.
  const isDailyRecurring = (entry: Income) =>
    Boolean(entry.isRecurring) &&
    (entry.recurrencePattern || "").toLowerCase().startsWith("daily");
  const prospectiveIncome = filteredIncome.filter(
    (entry) =>
      (entry.type || "Actual") === "Prospective" && !isDailyRecurring(entry)
  );
  const nonProspectiveIncome = filteredIncome.filter(
    (entry) =>
      (entry.type || "Actual") !== "Prospective" || isDailyRecurring(entry)
  );
  const pastMonths = incomeUtils.getPastMonthsIncomeBuckets(
    nonProspectiveIncome,
    12
  );
  const categorizedIncome = incomeUtils.categorizeIncome(nonProspectiveIncome);
  console.log("[IncomePage] Income section counts:", {
    received: categorizedIncome.received.length,
    pendingDeposit: categorizedIncome.pendingDeposit.length,
    overdue: categorizedIncome.overdue.length,
    today: categorizedIncome.today.length,
    tomorrow: categorizedIncome.tomorrow.length,
    thisWeek: categorizedIncome.thisWeek.length,
    next10Days: categorizedIncome.next10Days.length,
    thisMonth: categorizedIncome.thisMonth.length,
    nextMonth: categorizedIncome.nextMonth.length,
    expected: categorizedIncome.expected.length,
    upcoming: categorizedIncome.upcoming.length,
  });
  const sectionTotals =
    incomeUtils.calculateIncomeSectionTotals(categorizedIncome);

  // Categorize expenses for net calculation (filter out skipped for totals)
  const activeExpenses = expenses.filter((exp) => !exp.isSkipped);
  const categorizedExpenses = expenseUtils.categorizeExpenses(expenses); // Keep all for display
  const categorizedActiveExpenses =
    expenseUtils.categorizeExpenses(activeExpenses); // For totals
  const expenseSectionTotals = expenseUtils.calculateSectionTotals(
    categorizedActiveExpenses
  );

  // Create a map of expense totals by section for easy lookup
  const expenseTotalsBySection: Record<string, number> = {};
  expenseSectionTotals.forEach((s) => {
    expenseTotalsBySection[s.section] = s.total;
  });

  // Filter sections for display cards (remove upcoming, thisMonth, and nextMonth)
  const filteredSections = sectionTotals.filter(
    (s) =>
      s.section !== "upcoming" &&
      s.section !== "thisMonth" &&
      s.section !== "nextMonth"
  );

  const sectionDisplayOrder: ExpenseSection[] = [
    "received",
    "pendingDeposit",
    "overdue",
    "today",
    "tomorrow",
    "thisWeek",
    "next10Days",
    "expected",
  ];
  const visibleSections = sectionDisplayOrder
    .map((section) => filteredSections.find((s) => s.section === section))
    .filter((section): section is (typeof filteredSections)[number] =>
      Boolean(section)
    );
  const currentMonthName = incomeUtils.getCurrentMonthName();
  const nextMonthName = incomeUtils.getNextMonthName();
  console.log("[IncomePage] Month labels:", {
    currentMonth: currentMonthName,
    nextMonth: nextMonthName,
  });

  const getSectionColor = (section: string) => {
    switch (section) {
      case "received":
        return "#9CA3AF"; // Gray for received
      case "pendingDeposit":
        return "#10B981"; // Green for incoming
      case "overdue":
        return "#22C55E"; // Green for incoming
      case "today":
        return "#34D399"; // Green
      case "tomorrow":
        return "#10B981"; // Green
      case "thisWeek":
        return "#059669"; // Green
      case "next10Days":
        return "#047857"; // Green
      case "thisMonth":
        return "#065F46"; // Green
      case "nextMonth":
        return "#064E3B"; // Green
      case "expected":
        return "#14532D"; // Green
      default:
        return "#10B981"; // Green
    }
  };

  const getSectionValueColor = (section: string, total: number) => {
    if (Math.abs(total) < 0.005) return "#FFFFFF";
    if (section === "received") {
      return isDarkMode
        ? "rgba(226, 232, 240, 0.65)"
        : "rgba(45, 55, 72, 0.65)";
    }
    return "#10B981";
  };

  const getSectionLabel = (section: string) => {
    switch (section) {
      case "received":
        return "Received";
      case "pendingDeposit":
        return "Processing";
      case "overdue":
        return "Overdue";
      case "today":
        return "Today";
      case "tomorrow":
        return "Tomorrow";
      case "thisWeek":
        return "Within 7 Days";
      case "next10Days":
        return "Within 10 Days";
      case "expected":
        return "Within 30 Days";
      default:
        return section;
    }
  };

  const brandFieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: isDarkMode ? "#2d2d2d" : "white",
      color: isDarkMode ? "#e2e8f0" : "#1a202c",
      "& fieldset": {
        borderColor: isDarkMode ? "#444" : "#e2e8f0",
      },
      "&:hover fieldset": {
        borderColor: isDarkMode ? "#555" : "#38B2AC",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#38B2AC",
        boxShadow: "0 0 0 2px rgba(56, 178, 172, 0.2)",
      },
    },
    "& .MuiInputBase-input": {
      color: isDarkMode ? "#e2e8f0" : "#1a202c",
    },
    "& .MuiInputBase-input::placeholder": {
      color: isDarkMode ? "#a0aec0" : "#718096",
      opacity: 1,
    },
    "& .MuiInputLabel-root": {
      color: isDarkMode ? "#a0aec0" : "#4a5568",
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: "#38B2AC",
    },
  };

  const IncomeLoadingPlaceholder = () => {
    const placeholderCards = [
      { title: "Received", color: "#9CA3AF" },
      { title: "Processing", color: "#10B981" },
      { title: "Overdue", color: "#22C55E" },
      { title: "Today", color: "#34D399" },
      { title: "Tomorrow", color: "#10B981" },
      { title: "Within 7 Days", color: "#059669" },
      { title: "Within 10 Days", color: "#047857" },
      { title: "Rest of the Month", color: "#065F46" },
      { title: "Next Month", color: "#064E3B" },
      { title: "Next 3 Months", color: "#14532D" },
    ];

    const ValueSkeleton = ({ width = "65%" }: { width?: string | number }) => (
      <Box
        sx={{ display: "flex", alignItems: "baseline", gap: 0.5, minWidth: 0 }}
      >
        <Typography sx={{ fontSize: "20px", fontWeight: "bold" }}>$</Typography>
        <Skeleton
          variant="text"
          width={width}
          height={30}
          sx={{ maxWidth: "100%" }}
        />
      </Box>
    );

    return (
      <>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "repeat(2, minmax(0, 1fr))",
              sm: "repeat(3, minmax(0, 1fr))",
              md: "repeat(4, minmax(0, 1fr))",
              lg: `repeat(${placeholderCards.length}, minmax(0, 1fr))`,
            },
            gap: 2,
            marginBottom: 3,
          }}
        >
          {placeholderCards.map((card) => (
            <Box
              key={`income-cards-skeleton-${card.title}`}
              sx={{
                backgroundColor: isDarkMode ? "#1a1a1a" : "white",
                padding: "16px",
                borderRadius: "8px",
                boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                borderTop: `4px solid ${card.color}`,
                minWidth: 0,
                overflow: "hidden",
              }}
            >
              <Typography
                sx={{ fontSize: "14px", color: "#718096", marginBottom: "4px" }}
              >
                {card.title}
              </Typography>
              <ValueSkeleton />
              <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: 600,
                    color: "#718096",
                  }}
                >
                  $
                </Typography>
                <Skeleton
                  variant="text"
                  width="55%"
                  height={16}
                  sx={{ maxWidth: "100%" }}
                />
              </Box>
            </Box>
          ))}
        </Box>

        <SearchFilterBar
          searchQuery={searchQuery}
          onSearchQueryChange={setSearchQuery}
          onSearchApply={handleSearchApply}
          searchPlaceholder="Search client or invoice"
          filters={[
            {
              key: "paid",
              label: "Paid",
              value: paidFilter,
              onChange: (value) => setPaidFilter(value as typeof paidFilter),
              options: [
                { value: "all", label: "All" },
                { value: "paid", label: "Paid" },
                { value: "unpaid", label: "Unpaid" },
              ],
              minWidth: 120,
            },
            {
              key: "recurring",
              label: "Recurring",
              value: recurringFilter,
              onChange: (value) =>
                setRecurringFilter(value as typeof recurringFilter),
              options: [
                { value: "all", label: "All" },
                { value: "recurring", label: "Recurring" },
                { value: "one-time", label: "One-time" },
              ],
              minWidth: 140,
            },
          ]}
          sharedToggle={{
            value: sharedOnly,
            onToggle: () => setSharedOnly((prev) => !prev),
          }}
          expandToggle={{
            value: allExpanded,
            onToggle: () => {
              setAllExpanded((prev) => !prev);
              setExpandedSection(null);
            },
          }}
        />

        <Box sx={{ mt: 3 }}>
          {Array.from({ length: 4 }).map((_, idx) => (
            <Box key={`income-section-skeleton-${idx}`} sx={{ mb: 2 }}>
              <Box
                sx={{
                  backgroundColor: isDarkMode ? "#1a1a1a" : "white",
                  px: 1.5,
                  py: 1.25,
                  borderRadius: "8px 8px 0 0",
                  boxShadow: isDarkMode
                    ? "0 1px 3px rgba(0,0,0,0.5)"
                    : "0 1px 3px rgba(0,0,0,0.1)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  borderLeft: "4px solid #CBD5E0",
                }}
              >
                <Skeleton variant="text" width="40%" height={22} />
                <Skeleton variant="text" width="20%" height={22} />
              </Box>
              <Box
                sx={{
                  mt: 0,
                  backgroundColor: isDarkMode ? "#252525" : "#f7f8fa",
                  px: 2,
                  py: 2,
                  borderRadius: "0 0 8px 8px",
                  boxShadow: isDarkMode
                    ? "0 1px 3px rgba(0,0,0,0.5)"
                    : "0 1px 3px rgba(0,0,0,0.1)",
                }}
              >
                {Array.from({ length: 3 }).map((__, rowIdx) => (
                  <Box
                    key={`income-row-skeleton-${idx}-${rowIdx}`}
                    sx={{
                      backgroundColor: isDarkMode ? "#1a1a1a" : "white",
                      p: 1.5,
                      borderRadius: 1,
                      boxShadow: isDarkMode
                        ? "0 2px 4px rgba(0, 0, 0, 0.3)"
                        : "0 1px 2px rgba(0,0,0,0.05)",
                      mb: 1,
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 2,
                      alignItems: "center",
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Skeleton variant="text" width="55%" height={20} />
                      <Skeleton variant="text" width="35%" height={16} />
                    </Box>
                    <Box sx={{ width: 140, textAlign: "right" }}>
                      <Skeleton variant="text" width="80%" height={20} />
                      <Skeleton variant="text" width="60%" height={16} />
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      </>
    );
  };

  return (
    <>
      {/* Main Content */}
      <Box sx={{ maxWidth: 1200, margin: "0 auto", padding: { xs: 2, md: 3 } }}>
        {isLoading ? (
          <IncomeLoadingPlaceholder />
        ) : (
          <>
            {/* Section Cards Grid */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "repeat(2, minmax(0, 1fr))",
                  sm: "repeat(3, minmax(0, 1fr))",
                  md: "repeat(4, minmax(0, 1fr))",
                  lg: `repeat(${visibleSections.length}, minmax(0, 1fr))`,
                },
                gap: 2,
                marginBottom: 3,
              }}
            >
              {visibleSections.map((section) => {
                const expenseTotal =
                  expenseTotalsBySection[section.section] || 0;
                const netValue = section.total - expenseTotal;
                const isPositive = netValue >= 0;
                const sectionExpenses =
                  categorizedExpenses[section.section as ExpenseSection] || [];
                const isSelected = expandedSection === section.section;

                const expenseTooltipContent =
                  sectionExpenses.length > 0 ? (
                    <Box sx={{ maxWidth: 320 }}>
                      <Typography
                        sx={{
                          fontSize: "11px",
                          fontWeight: 600,
                          mb: 0.5,
                          borderBottom: "1px solid #444",
                          pb: 0.5,
                        }}
                      >
                        Expenses (
                        {sectionExpenses.filter((e) => !e.isSkipped).length}{" "}
                        active)
                      </Typography>
                      {sectionExpenses
                        .filter((e) => !e.isSkipped)
                        .slice(0, 6)
                        .map((exp) => {
                          const dueDate =
                            exp.dueDate instanceof Date
                              ? exp.dueDate
                              : new Date(exp.dueDate);
                          const dueDateStr = `${(dueDate.getUTCMonth() + 1).toString().padStart(2, "0")}/${dueDate.getUTCDate().toString().padStart(2, "0")}`;
                          return (
                            <Box
                              key={exp.id}
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                fontSize: "11px",
                                py: 0.25,
                                gap: 1,
                              }}
                            >
                              <span style={{ color: "#9CA3AF", flexShrink: 0 }}>
                                {dueDateStr}
                              </span>
                              <span
                                style={{
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  flex: 1,
                                }}
                              >
                                {exp.name}
                              </span>
                              <span
                                style={{
                                  fontWeight: 600,
                                  flexShrink: 0,
                                  color: "#E53E3E",
                                }}
                              >
                                -{expenseUtils.formatCurrency(exp.amount)}
                              </span>
                            </Box>
                          );
                        })}
                    </Box>
                  ) : (
                    "No expenses"
                  );

                return (
                  <Box
                    key={section.section}
                    onClick={() => {
                      if (expandedSection === section.section) {
                        setExpandedSection(null);
                        updateSectionParam(null);
                      } else {
                        setExpandedSection(section.section as ExpenseSection);
                        updateSectionParam(section.section as ExpenseSection);
                      }
                    }}
                    sx={{
                      backgroundColor: isDarkMode ? "#1a1a1a" : "white",
                      padding: "16px",
                      borderRadius: "8px",
                      boxShadow: isSelected
                        ? `0 0 0 2px ${getSectionColor(section.section)}`
                        : "0 1px 2px rgba(0,0,0,0.05)",
                      borderTop: `4px solid ${getSectionColor(section.section)}`,
                      cursor: "pointer",
                      transition:
                        "transform 0.1s ease-in-out, box-shadow 0.1s ease-in-out",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: isSelected
                          ? `0 4px 6px rgba(0,0,0,0.1), 0 0 0 2px ${getSectionColor(section.section)}`
                          : "0 4px 6px rgba(0,0,0,0.1)",
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "14px",
                        color: "#718096",
                        marginBottom: "4px",
                      }}
                    >
                      {getSectionLabel(section.section)}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "20px",
                        fontWeight: "bold",
                        color: getSectionValueColor(
                          section.section,
                          section.total
                        ),
                      }}
                    >
                      {incomeUtils.formatCurrency(section.total)}
                    </Typography>
                    {expenseTotal > 0 && (
                      <Tooltip
                        title={expenseTooltipContent}
                        arrow
                        placement="bottom"
                        componentsProps={{
                          tooltip: {
                            sx: {
                              bgcolor: "#1a1a1a",
                              "& .MuiTooltip-arrow": { color: "#1a1a1a" },
                            },
                          },
                        }}
                      >
                        <Typography
                          onClick={(e) => e.stopPropagation()}
                          sx={{
                            fontSize: "12px",
                            fontWeight: 600,
                            color: isPositive ? "#38A169" : "#E53E3E",
                            marginTop: "4px",
                            display: "inline-block",
                          }}
                        >
                          {isPositive ? "+" : ""}
                          {incomeUtils.formatCurrency(netValue)}
                        </Typography>
                      </Tooltip>
                    )}
                  </Box>
                );
              })}
            </Box>

            {/* Search, filters, and toggles */}
            <SearchFilterBar
              searchQuery={searchQuery}
              onSearchQueryChange={setSearchQuery}
              onSearchApply={handleSearchApply}
              searchPlaceholder="Search client or invoice"
              filters={[
                {
                  key: "paid",
                  label: "Paid",
                  value: paidFilter,
                  onChange: (value) =>
                    setPaidFilter(value as typeof paidFilter),
                  options: [
                    { value: "all", label: "All" },
                    { value: "paid", label: "Paid" },
                    { value: "unpaid", label: "Unpaid" },
                  ],
                  minWidth: 120,
                },
                {
                  key: "recurring",
                  label: "Recurring",
                  value: recurringFilter,
                  onChange: (value) =>
                    setRecurringFilter(value as typeof recurringFilter),
                  options: [
                    { value: "all", label: "All" },
                    { value: "recurring", label: "Recurring" },
                    { value: "one-time", label: "One-time" },
                  ],
                  minWidth: 140,
                },
              ]}
              sharedToggle={{
                value: sharedOnly,
                onToggle: () => setSharedOnly((prev) => !prev),
              }}
              expandToggle={{
                value: allExpanded,
                onToggle: () => {
                  setAllExpanded((prev) => !prev);
                  setExpandedSection(null);
                },
              }}
              refreshAction={{
                onRefresh: () => refreshFromTpc(true),
                busy: isSyncingTpc,
                tooltip: "Refresh from TPC Account Center",
              }}
            />

            {/* Income Lists or Empty State */}
            {income.length === 0 ? (
              <EmptyState
                icon={<AccountBalanceWalletIcon />}
                title="No Income Yet"
                description="Start tracking your income sources to better understand your cash flow and plan your finances."
                buttonText="Add Your First Income"
                onAction={onOpen}
              />
            ) : (
              <IncomeList
                income={categorizedIncome}
                sectionTotals={sectionTotals}
                pastMonths={pastMonths}
                prospectiveIncome={prospectiveIncome}
                onDeleteIncome={handleDeleteIncome}
                onShareIncome={handleShareIncome}
                onMarkReceived={handleMarkReceived}
                onSkipIncome={handleSkipIncome}
                onEditIncome={handleEditIncome}
                onSuspendIncome={handlePostponeIncome}
                onRescheduleIncome={handleRescheduleIncome}
                expandedSection={expandedSection}
                onSectionChange={setExpandedSection}
                allExpanded={allExpanded}
              />
            )}
          </>
        )}

        {/* Suspended income — excluded from totals, resumable from here. */}
        {suspendedIncome.length > 0 && (
          <Box
            sx={{
              mt: 4,
              borderRadius: 2,
              overflow: "hidden",
              border: `1px solid ${isDarkMode ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.12)"}`,
            }}
          >
            <Box
              sx={{
                px: 2,
                py: 1.25,
                display: "flex",
                alignItems: "center",
                gap: 1,
                backgroundColor: isDarkMode ? "#2d2d2d" : "#f3f4f6",
              }}
            >
              <Typography sx={{ fontWeight: 700 }}>Suspended</Typography>
              <Typography
                variant="caption"
                sx={{
                  backgroundColor: "#D69E2E20",
                  color: "#D69E2E",
                  px: 1,
                  py: 0.25,
                  borderRadius: "12px",
                  fontWeight: 600,
                }}
              >
                {suspendedIncome.length}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: isDarkMode ? "#9ca3af" : "#6b7280", ml: 1 }}
              >
                Not counted in income totals
              </Typography>
            </Box>
            {suspendedIncome.map((inc, idx) => (
              <Box
                key={inc.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  px: 2,
                  py: 1,
                  flexWrap: "wrap",
                  borderTop:
                    idx === 0
                      ? "none"
                      : `1px solid ${isDarkMode ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"}`,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    flexWrap: "wrap",
                    minWidth: 0,
                  }}
                >
                  <Typography sx={{ fontWeight: 600 }}>{inc.client}</Typography>
                  {inc.expectedByDate && (
                    <Typography
                      variant="caption"
                      sx={{ color: isDarkMode ? "#9ca3af" : "#6b7280" }}
                    >
                      {new Date(inc.expectedByDate).toLocaleDateString()}
                    </Typography>
                  )}
                  <Typography
                    variant="caption"
                    sx={{ color: "#D69E2E", fontWeight: 600 }}
                  >
                    {inc.suspendedUntil
                      ? `Until ${new Date(inc.suspendedUntil).toLocaleDateString()}`
                      : "Indefinitely"}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                  <Typography sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                    {incomeUtils.formatCurrency(Number(inc.invoiceTotal || 0))}
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleUnsuspendIncome(inc)}
                  >
                    Unsuspend
                  </Button>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* Add Income Form */}
      {isOpen && (
        <Drawer
          anchor="right"
          open={isOpen}
          onClose={onClose}
          PaperProps={{
            sx: {
              width: 400,
              padding: 2,
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.text.primary,
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {isEditing ? "Edit Income" : "Add Income"}
            </Typography>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
          <IncomeForm
            onAddIncome={handleAddIncome}
            onUpdateIncome={handleUpdateIncome}
            incomeToEdit={currentIncome}
            isEditing={isEditing}
          />
        </Drawer>
      )}

      {/* Received Modal */}
      {isReceivedModalOpen && (
        <div
          style={{
            position: "fixed",
            top: "0",
            left: "0",
            right: "0",
            bottom: "0",
            backgroundColor: "rgba(0,0,0,0.5)",
            zIndex: "2000",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              backgroundColor: isDarkMode ? "#1a1a1a" : "white",
              padding: "24px",
              borderRadius: "8px",
              boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
              width: "400px",
              maxWidth: "90vw",
            }}
          >
            <h3 style={{ marginBottom: "12px", fontSize: "18px" }}>
              Mark as Received - {incomeToMarkReceived?.client}
            </h3>

            <p style={{ marginBottom: "16px", fontSize: "14px" }}>
              What date will the funds be deposited?
            </p>

            <div style={{ marginBottom: "16px" }}>
              <TextField
                label="Date Received"
                type="datetime-local"
                value={receivedDate}
                onChange={(e) => {
                  const nextValue = normalizeDateTimeInput(e.target.value);
                  setReceivedDate(nextValue);
                  console.log("[IncomePage] Received date changed:", {
                    incomeId: incomeToMarkReceived?.id,
                    receivedDate: nextValue,
                  });
                }}
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                sx={brandFieldSx}
              />
            </div>

            <div style={{ marginBottom: "24px" }}>
              <TextField
                label="Deposit Date *"
                type="datetime-local"
                value={depositDate}
                onChange={(e) => {
                  const nextValue = normalizeDateTimeInput(e.target.value);
                  setDepositDate(nextValue);
                  console.log("[IncomePage] Deposit date changed:", {
                    incomeId: incomeToMarkReceived?.id,
                    depositDate: nextValue,
                  });
                }}
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
                sx={brandFieldSx}
              />
            </div>

            <div style={{ marginBottom: "24px" }}>
              <Autocomplete
                value={paymentProcessor ? { label: paymentProcessor } : null}
                inputValue={paymentProcessorInput}
                onInputChange={(_, newInputValue) => {
                  setPaymentProcessorInput(newInputValue);
                  setPaymentProcessor(newInputValue);
                  console.log("[IncomePage] Payment processor input:", {
                    incomeId: incomeToMarkReceived?.id,
                    value: newInputValue,
                  });
                }}
                onChange={(_, newValue) => {
                  let nextValue = "";
                  if (typeof newValue === "string") {
                    nextValue = newValue;
                  } else if (newValue?.inputValue) {
                    nextValue = newValue.inputValue;
                  } else if (newValue?.label) {
                    nextValue = newValue.label;
                  }
                  setPaymentProcessor(nextValue);
                  setPaymentProcessorInput(nextValue);
                  console.log("[IncomePage] Payment processor selected:", {
                    incomeId: incomeToMarkReceived?.id,
                    value: nextValue,
                  });
                }}
                filterOptions={(options, params) => {
                  const filtered = paymentProcessorFilter(options, params);
                  const inputValue = params.inputValue.trim();
                  const isExisting = options.some(
                    (option) =>
                      option.label.toLowerCase() === inputValue.toLowerCase()
                  );
                  if (inputValue !== "" && !isExisting) {
                    filtered.push({
                      inputValue,
                      label: `Add "${inputValue}"`,
                    });
                  }
                  return filtered;
                }}
                options={paymentProcessorOptions}
                getOptionLabel={(option) => {
                  if (typeof option === "string") return option;
                  if (option.inputValue) return option.inputValue;
                  return option.label;
                }}
                isOptionEqualToValue={(option, value) =>
                  option.label === value.label
                }
                renderOption={(props, option) => (
                  <li {...props}>{option.label}</li>
                )}
                selectOnFocus
                handleHomeEndKeys
                freeSolo
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Payment Processor"
                    placeholder="Type to search or add..."
                    size="small"
                    fullWidth
                    sx={brandFieldSx}
                  />
                )}
              />
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "8px",
              }}
            >
              <button
                onClick={() => {
                  setIsReceivedModalOpen(false);
                  setIncomeToMarkReceived(undefined);
                  setReceivedDate("");
                  setDepositDate("");
                  setPaymentProcessor("");
                  setPaymentProcessorInput("");
                  console.log("[IncomePage] Closed mark received modal");
                }}
                style={{
                  padding: "8px 16px",
                  backgroundColor: "#f7fafc",
                  border: "1px solid #e2e8f0",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleReceivedSubmit}
                style={{
                  padding: "8px 16px",
                  backgroundColor: "#10B981",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontWeight: "bold",
                }}
              >
                Mark Received
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Floating Action Buttons */}
      <FloatingActionMenu label="Income actions">
        {/* Add Income FAB */}
        <Tooltip title="Add Income" placement="left" arrow>
          <Fab color="primary" size="large" aria-label="add" onClick={onOpen}>
            <AddIcon />
          </Fab>
        </Tooltip>
      </FloatingActionMenu>
      <ShareRecordModal
        open={shareOpen}
        onClose={() => {
          setShareOpen(false);
          setShareIncome(null);
        }}
        groups={groups}
        onShare={handleShareRecord}
      />
      <PageShareModal
        open={pageShareOpen}
        onClose={() => {
          setPageShareOpen(false);
          const next = new URLSearchParams(searchParams);
          next.delete("share");
          setSearchParams(next, { replace: true });
        }}
        pageType="income"
        incomeItems={income}
      />

      {/* Undo Notification */}
      <Snackbar
        open={undoSnackbar.open}
        autoHideDuration={5000}
        onClose={() => setUndoSnackbar({ open: false, message: "" })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        message={undoSnackbar.message}
        action={
          undoSnackbar.onUndo ? (
            <Button
              size="small"
              sx={{ color: "#90cdf4" }}
              onClick={() => {
                const undo = undoSnackbar.onUndo;
                setUndoSnackbar({ open: false, message: "" });
                undo?.();
              }}
            >
              Undo
            </Button>
          ) : undefined
        }
      />

      {/* Move a single occurrence to a later date. */}
      <Dialog
        open={Boolean(incomeToReschedule)}
        onClose={() => setIncomeToReschedule(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Move to a later date</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {incomeToReschedule?.isRecurringInstance
              ? "This occurrence moves on its own — the rest of the series keeps its schedule."
              : "Sets a new expected date for this income."}
          </Typography>
          <TextField
            type="date"
            fullWidth
            size="small"
            label="Expected on"
            InputLabelProps={{ shrink: true }}
            value={rescheduleTo}
            onChange={(e) => setRescheduleTo(e.target.value)}
          />
          {rescheduleError && (
            <Typography variant="body2" sx={{ color: "#E53E3E", mt: 2 }}>
              {rescheduleError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIncomeToReschedule(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!rescheduleTo}
            onClick={applyReschedule}
          >
            Move
          </Button>
        </DialogActions>
      </Dialog>

      {/* Postpone — hold a record out of the totals, resumable from the
          Suspended section below. */}
      <Dialog
        open={Boolean(incomeToPostpone)}
        onClose={() => setIncomeToPostpone(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Postpone income</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {incomeToPostpone?.client} stays listed under Suspended and is left
            out of income totals until it resumes.
          </Typography>
          <TextField
            type="date"
            fullWidth
            size="small"
            label="Postpone until"
            InputLabelProps={{ shrink: true }}
            value={postponeUntil}
            onChange={(e) => setPostponeUntil(e.target.value)}
          />
          {postponeError && (
            <Typography variant="body2" sx={{ color: "#E53E3E", mt: 2 }}>
              {postponeError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ flexWrap: "wrap", gap: 1 }}>
          <Button onClick={() => setIncomeToPostpone(null)}>Cancel</Button>
          <Button onClick={() => applyPostpone("indefinite")}>
            Postpone indefinitely
          </Button>
          <Button
            variant="contained"
            disabled={!postponeUntil}
            onClick={() => applyPostpone("until")}
          >
            Postpone until date
          </Button>
        </DialogActions>
      </Dialog>

      <LoadingTimerDebug
        loading={isLoading}
        label="Income"
        details={{
          income: income.length,
          expenses: expenses.length,
          groups: groups.length,
          sharedOnly,
          drawerOpen: isOpen,
          editing: isEditing,
          expandedSection,
          allExpanded,
          demoMode: demoService.isDemo(),
        }}
      />
    </>
  );
};

export default IncomePage;
